import React, { useEffect, useRef } from "react";
import { EditorState, Transaction } from "prosemirror-state";
import { EditorView } from "prosemirror-view";
import { Schema } from "prosemirror-model";
import { schema } from "prosemirror-schema-basic";
import {
  defaultMarkdownParser,
  defaultMarkdownSerializer,
} from "prosemirror-markdown";
import { addListNodes } from "prosemirror-schema-list";
import { exampleSetup } from "prosemirror-example-setup";
import "prosemirror-example-setup/style/style.css";
import "prosemirror-view/style/prosemirror.css";
import "prosemirror-menu/style/menu.css";
import "prosemirror-gapcursor/style/gapcursor.css";
import "../../../../assets/prosemirror.css";
import { Box } from "@chakra-ui/react";
import { t } from "i18next";
import { toast } from "../../../shared/views/CustomToaster";

const myNodes = schema.spec.nodes
  .remove("image")
  .remove("horizontal_rule")
  .remove("code_block")
  .remove("hard_break")
  .remove("link");

const customSchema = new Schema({
  nodes: addListNodes(myNodes, "paragraph block*", "block"),
  marks: schema.spec.marks.remove("code").remove("link"),
});

export const ProseMirrorWrapper: React.FC<{
  defaultContent?: string;
  onChange: (markdown: string) => void;
}> = ({ defaultContent = "", onChange }) => {
  const editorRef = useRef<EditorView>();
  const editorDom = useRef(null);


  const isNotImage = (transaction: Transaction) =>
    transaction.doc.content.lastChild?.content.lastChild?.type.isText ||
    transaction.doc.content.lastChild?.content.lastChild?.type === undefined;

  useEffect(() => {
    if (editorRef.current) return;

    // set defaultContent
    const initialElement = document.createElement("div");
    initialElement.textContent = defaultContent;

    const state = EditorState.create({
      doc: defaultMarkdownParser.parse(defaultContent)!,
      plugins: exampleSetup({ schema: customSchema }),
    });

    editorRef.current = new EditorView(editorDom.current, {
      state: state,
      dispatchTransaction: (transaction) => {
        if (editorRef.current) {
          if (isNotImage(transaction)) {
            // dispatch transaction normally
            const newState = editorRef.current.state.apply(transaction);
            editorRef.current.updateState(newState);

            // get the markdown value and call onChange with it
            const markdown = defaultMarkdownSerializer
              .serialize(transaction.doc)
              .trim();

            onChange(markdown);
          } else {
            toast("error", t(`map:comments.imageSubmit`));
          }
        }
      },
    });
    editorRef.current.dom.focus();
    //eslint-disable-next-line
  }, [defaultContent, editorDom.current]);

  useEffect(() => {
    return () => {
      editorRef.current?.destroy();
      editorRef.current = undefined;
    };
    //eslint-disable-next-line
  }, []);

  return <Box py={2} onKeyDown={(e) => e.stopPropagation()} ref={editorDom} />;
};
